import { useEffect, useState } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import type { Engine, ISourceOptions } from "@tsparticles/engine";
import { useEnhancedAnimations } from "@/hooks/useMotionPreferences";

export function ParticlesBackground() {
  const [init, setInit] = useState(false);
  const enhancedAnimationsEnabled = useEnhancedAnimations();

  useEffect(() => {
    if (!enhancedAnimationsEnabled) return;

    initParticlesEngine(async (engine: Engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, [enhancedAnimationsEnabled]);

  const particlesConfig: ISourceOptions = {
    background: {
      color: { value: "transparent" },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: "grab",
        },
        onClick: {
          enable: true,
          mode: "push",
        },
      },
      modes: {
        grab: {
          distance: 140,
          links: {
            opacity: 0.5,
            color: "#00d9ff",
          },
        },
        push: {
          quantity: 2,
        },
      },
    },
    particles: {
      color: {
        value: "#00d9ff",
      },
      links: {
        color: "#00d9ff",
        distance: 150,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      move: {
        enable: true,
        speed: 1,
        direction: "none",
        random: false,
        straight: false,
        outModes: {
          default: "bounce",
        },
      },
      number: {
        density: {
          enable: true,
        },
        value: 60,
      },
      opacity: {
        value: 0.5,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 3 },
      },
    },
    detectRetina: true,
  };

  // Render static fallback for reduced-motion or touch devices
  if (!enhancedAnimationsEnabled) {
    return (
      <div 
        className="fixed inset-0 -z-10 pointer-events-none animated-gradient"
        data-testid="particles-fallback"
      />
    );
  }

  // Render particles only when initialized
  if (!init) {
    return (
      <div 
        className="fixed inset-0 -z-10 pointer-events-none animated-gradient"
        data-testid="particles-loading"
      />
    );
  }

  return (
    <Particles
      id="tsparticles-background"
      options={particlesConfig}
      className="fixed inset-0 -z-10 pointer-events-none"
      data-testid="particles-background"
    />
  );
}
